import React from 'react';
import EditorWrapper from "../EditorWrapper/EditorWrapper";
import {normalizeContentHtml} from "../EditorWrapper/normalizeContentHtml";
import Icon from "../Icon";
import svgSortAsc from "../../svgs/arrow-up.svg";
import {getData, manageData} from "../../requests/CRUD";
import {toMediaUrl, sortArray} from "../../utils";
import {TRAINING, TRAINING_CONTENT} from "../../consts/tables";
import {getChapterColorBySlug} from "../Training/chapterColors";
import BrokerageMap from "../BrokerageMap";
import LinkComp from '../LinkComp'
import { canEditTraining } from '../../access';

export default class ContentPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            title: '',
            lessonIndex: 1,
            image: '',
            backButton: '',
            subchapterParentSlug: '',
            isEdit: false,
            rowId: '',
            brokerageChapters: [],
        };

        this.editorRef = React.createRef();
        this.contentRef = React.createRef();

        this.saveEditorContent = this.saveEditorContent.bind(this);
        this.cancelEditorChanges = this.cancelEditorChanges.bind(this);
        this.toggleEdit = this.toggleEdit.bind(this);
    }

    // Lesson content is author-controlled rich text and not every link was saved
    // with target="_blank" (older entries, or an editor Target dropdown left on
    // its default). Force every in-content link to open in a new tab so reading
    // a lesson never navigates the trainee away from it.
    openContentLinksInNewTab() {
        if (!this.contentRef.current) return;
        this.contentRef.current.querySelectorAll('a[href]').forEach(a => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        });
    }

    componentDidMount() {
        const keys = Object.keys(this.props.params);
        const slugs = this.props.params && keys.length ? keys.map(key => this.props.params[key]) : [];
        const subchapterParentSlug = slugs.join('/');
        const slug = slugs[slugs.length - 1] || '';
        const parentChapterSlug = slugs[slugs.length - 2] || '';
        slugs.pop();
        const parentSlug = slugs.join('/') || 'training';
        const parentSlugCopy = [...slugs];
        slugs.pop();
        const parentChapterParentSlug = slugs.join('/') || 'training';

        // On any sub-page of brokerage-continuum, load the continuum stations so the
        // diagram can be rendered above the lesson. BrokerageMap falls back to its
        // built-in stations when this comes back empty (e.g. unseeded data).
        const topSlug = Object.values(this.props.params)[0];
        if (topSlug === 'brokerage-continuum') {
            getData({
                tableCode: TRAINING,
                options: {
                    filter: [
                        { text: '', code: 'DELETED', type: 'null' },
                        { text: 'brokerage-continuum', code: 'PARENT_SLUG', type: 'exact' },
                    ],
                    filterType: 'and',
                },
            }).then(res => {
                if (res && res.success && Array.isArray(res.data) && res.data.length) {
                    const brokerageChapters = sortArray(
                        res.data.map(c => ({ id: c.SLUG, name: c.NAME, index: parseInt(c.INDEX) })),
                        'index');
                    this.setState({brokerageChapters});
                }
            });
        }

        Promise.all([
            getData({
                tableCode: TRAINING,
                options: {
                    column: [
                        'NAME',
                        'PHOTO',
                        'INDEX',
                    ],
                    filter: [{
                        text: '',
                        code: 'DELETED',
                        type: 'null',
                    }, {
                        text: parentSlug,
                        code: 'PARENT_SLUG',
                        type: 'exact',
                    }, {
                        text: slug,
                        code: 'SLUG',
                        type: 'exact',
                    }],
                    filterType: 'and',
                },
            }),
            getData({
                tableCode: TRAINING,
                options: {
                    column: [
                        'NAME',
                    ],
                    filter: [{
                        text: '',
                        code: 'DELETED',
                        type: 'null',
                    }, {
                        text: parentChapterParentSlug,
                        code: 'PARENT_SLUG',
                        type: 'exact',
                    }, {
                        text: parentChapterSlug,
                        code: 'SLUG',
                        type: 'exact',
                    }],
                    filterType: 'and',
                },
            }),
            getData({
                tableCode: TRAINING_CONTENT,
                options: {
                    column: [
                        'ARCIMEDES_ROW_ID',
                        'CONTENT',
                        'PHOTO'
                    ],
                    filter: [{
                        text: '',
                        code: 'DELETED',
                        type: 'null',
                    }, {
                        text: subchapterParentSlug,
                        code: 'CHAPTER_SLUG',
                        type: 'exact',
                    }],
                    filterType: 'and',
                },
            }),
        ]).then(res => {
            if (res && res[0] && res[0].success && res[1] && res[1].success && res[2] && res[2].success) {
                if (this.contentRef.current) {
                    // Lessons saved before the editor's paste cleanup carry Word's
                    // broken list nesting (a sub-list beside its <li>, which renumbers
                    // from 1) and Word's inline fonts. Repair on render so existing
                    // pages read correctly without anyone re-editing them.
                    this.contentRef.current.innerHTML = normalizeContentHtml(
                        res[2].data[0] ? res[2].data[0].CONTENT : '');
                    this.openContentLinksInNewTab();
                }
                // The chapter row may not exist yet — e.g. a brokerage-continuum station
                // (marketing, cold-call, …) that is shown on the map but not yet seeded in
                // TRAINING. Guard against it so the page renders an empty, editable lesson
                // instead of throwing on res[0].data[0] and showing nothing at all.
                const chapter = res[0].data[0] || {};
                const photo = res[2].data[0] && res[2].data[0].PHOTO ? res[2].data[0].PHOTO : chapter.PHOTO;
                const rowId = res[2].data[0] && res[2].data[0].ARCIMEDES_ROW_ID ? res[2].data[0].ARCIMEDES_ROW_ID : '';
                const fallbackTitle = slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
                this.setState({
                    title: chapter.NAME ? chapter.NAME.replace('<br/>', ' ') : fallbackTitle,
                    lessonIndex: chapter.INDEX ? parseInt(chapter.INDEX) : 1,
                    image: toMediaUrl(photo) || '',
                    backButton: res[1].data[0] && res[1].data[0].NAME || 'TRAINING',
                    subchapterParentSlug,
                    rowId,
                });
            }
        });
    }

    saveEditorContent() {
        if (this.editorRef.current) {
            const content = normalizeContentHtml(this.editorRef.current.getContent());
            manageData({
                tableCode: TRAINING_CONTENT,
                data: [{
                    ARCIMEDES_ROW_ID: this.state.rowId,
                    CHAPTER_SLUG: this.state.subchapterParentSlug,
                    CONTENT: content
                }]
            }).then(res => {
                if (res && res.success && this.contentRef.current) {
                    const newRowId = res.data && res.data[0] && res.data[0].ARCIMEDES_ROW_ID;
                    this.contentRef.current.innerHTML = content;
                    this.openContentLinksInNewTab();
                    this.setState({
                        rowId: newRowId || this.state.rowId
                    });
                    this.toggleEdit();
                    return;
                }
                alert(res.message || 'Unexpected error');
            });
        }
    }

    cancelEditorChanges() {
        if (this.editorRef.current && this.contentRef.current)
            this.editorRef.current.setContent(this.contentRef.current.innerHTML);
        this.toggleEdit();
    }

    toggleEdit() {
        this.setState(prevState => {
            return {
                isEdit: !prevState.isEdit
            }
        });
    }

    render() {
        const {title, lessonIndex, image, backButton, isEdit, brokerageChapters} = this.state;
        const params = this.props.params || {};
        const topSlug = params.slug || Object.values(params)[0];
        // Editing training follows the member's access level (training.edit); the Brokerage Continuum may be left out.
        const canEdit = canEditTraining(topSlug);
        const chapterColor = getChapterColorBySlug(topSlug);

        const chapterSlug = params.slug2 || '';
        // The "back" crumb must point to the immediate parent = current path minus the
        // last segment. For a 2-level section (/training/administration/company-holidays)
        // that's /training/administration; for a deeper brokerage lesson it's the parent
        // chapter. Using topSlug/chapterSlug wrongly pointed back to the current page.
        const slugSegments = [params.slug, params.slug2, params.slug3, params.slug4, params.slug5].filter(Boolean);
        const parentPath = `${arc.path.base}training/${slugSegments.slice(0, -1).join('/')}`;
        const isBrokerage = topSlug === 'brokerage-continuum';
        const courseLabel = isBrokerage ? 'Brokerage Continuum' : '';

        return (
            <div className='ContentPage' style={{'--chapter-color': chapterColor}}>
                <div className='lesson-page-shell'>
                    <nav className='training-breadcrumb'>
                        <LinkComp to={arc.path.base} className='training-breadcrumb__link'>Home</LinkComp>
                        <span className='training-breadcrumb__sep'>/</span>
                        <LinkComp to={`${arc.path.base}training`} className='training-breadcrumb__link'>Training</LinkComp>
                        
                        {courseLabel && (
                            <React.Fragment>
                                <span className='training-breadcrumb__sep'>/</span>
                                <LinkComp to={`${arc.path.base}training/${topSlug}`} className='training-breadcrumb__link'>
                                    {courseLabel}
                                </LinkComp>
                            </React.Fragment>
                        )}

                        {backButton && backButton !== 'TRAINING' && backButton !== courseLabel && (
                            <React.Fragment>
                                <span className='training-breadcrumb__sep'>/</span>
                                <LinkComp to={parentPath} className='training-breadcrumb__link'>
                                    {backButton}
                                </LinkComp>
                            </React.Fragment>
                        )}

                        {title && (
                            <React.Fragment>
                                <span className='training-breadcrumb__sep'>/</span>
                                <span className='training-breadcrumb__current'>{title}</span>
                            </React.Fragment>
                        )}
                    </nav>

                    {isBrokerage && (
                        <div className='training-map-container training-map-container--chapter'>
                            <BrokerageMap chapters={brokerageChapters} activeSlug={chapterSlug} />
                        </div>
                    )}

                    <div className='lesson-header-wrapper'>
                        <div className='content-wrapper'>
                            <div className='lesson-hero'>
                                <div className='lesson-hero__main'>
                                    <div className='lesson-meta-row'>
                                        {/* <div className='lesson-number-badge'>
                                            {String(lessonIndex).padStart(2, '0')}
                                        </div> */}
                                        <h1 className='lesson-title'>{title}</h1>
                                        {canEdit && (
                                            <div className='edit-controls'>
                                                {isEdit && (
                                                    <React.Fragment>
                                                        <div className='button-new negative' onClick={this.cancelEditorChanges}>Cancel</div>
                                                        <div className='button-new positive' onClick={this.saveEditorContent}>Save</div>
                                                    </React.Fragment>
                                                )}
                                                {!isEdit && (
                                                    this.props.allowSubchapters && this.props.onStartSubchapters ? (
                                                        <React.Fragment>
                                                            {/* Truly empty chapter (no content, no sub-chapters yet):
                                                                Edit opens the chapter editor to add sub-chapters —
                                                                the same editing flow as every other chapter page.
                                                                Writing it as a single lesson is the secondary path. */}
                                                            <div className='button-new positive' onClick={this.props.onStartSubchapters}>Edit</div>
                                                            <div className='button-new action' onClick={this.toggleEdit}>Write Content</div>
                                                        </React.Fragment>
                                                    ) : (
                                                        <div className='button-new positive' onClick={this.toggleEdit}>Edit</div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {/* <div className='lesson-eyebrow'>Lesson guide</div> */}
                                    {/* <div className='lesson-subtitle'>
                                        Review the key ideas, examples, and takeaways below, then come back whenever you need a quick refresher.
                                    </div> */}
                                </div>
                                {/* <div className='lesson-hero__aside'>
                                    <div className='lesson-side-card'>
                                        <div className='lesson-side-card__label'>Learning format</div>
                                        <div className='lesson-side-card__value'>Read in order</div>
                                        <div className='lesson-side-card__copy'>Designed for quick review and practical application.</div>
                                    </div>
                                    <div className='lesson-chip-row'>
                                        <div className='lesson-chip'>Step-by-step</div>
                                        <div className='lesson-chip'>Reference friendly</div>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>

                    <div className='page-body-container'>
                        <div className='content-wrapper'>
                            <div className='lesson-content-surface'>
                                {image && <img src={image} className='hero-image' alt={title}/>}
                                
                                <div className='article-content'>
                                    <div ref={this.contentRef} className={canEdit && isEdit ? 'hidden' : ''}/>
                                    <EditorWrapper
                                        content={this.contentRef.current && this.contentRef.current.innerHTML || ''}
                                        ref={this.editorRef}
                                        className={!canEdit || !isEdit ? 'hidden' : ''}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}